import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowRight, Check, Sparkles } from "lucide-react";
import { DoodleCode, DoodleGear, FloatingDoodle } from "./Doodles";
import { useInViewOnce } from "@/hooks/useInViewOnce";
import { useLanguage } from "@/contexts/LanguageContext";

const PricingSection = () => {
  const { ref, isInView } = useInViewOnce();
  const { t } = useLanguage();

  const includedFeatures = [
    t("landing.pricing_f1"),
    t("landing.pricing_f2"),
    t("landing.pricing_f3"),
    t("landing.pricing_f4"),
    t("landing.pricing_f5"),
    t("landing.pricing_f6"),
    t("landing.pricing_f7"),
    t("landing.pricing_f8"),
  ];

  return (
    <section id="pricing" className="relative py-24 bg-secondary/50 overflow-hidden">
      {/* Doodle decorations */}
      <div className="pointer-events-none absolute inset-0">
        <FloatingDoodle delay={0.3} duration={5}>
          <DoodleCode className="absolute top-10 left-[5%] text-primary/10" />
        </FloatingDoodle>
        <FloatingDoodle delay={1.5} duration={4} y={6}>
          <DoodleGear className="absolute bottom-16 right-[6%] text-accent/12" />
        </FloatingDoodle>
      </div>

      <div className="container relative mx-auto px-4">
        <div
          ref={ref}
          className="mx-auto max-w-3xl rounded-2xl border border-border bg-card p-8 shadow-card md:p-12"
          style={{
            opacity: isInView ? 1 : 0,
            transform: isInView ? "translateY(0)" : "translateY(20px)",
            transition: "opacity 0.5s ease-out, transform 0.5s ease-out",
          }}
        >
          <div className="flex flex-col gap-8 md:flex-row md:items-start md:gap-12">
            {/* Left: pricing info */}
            <div className="flex-1">
              <div className="inline-flex items-center gap-1.5 rounded-full bg-accent/10 px-3 py-1 text-xs font-semibold text-accent mb-4">
                <Sparkles className="h-3 w-3" />
                {t("landing.pricing_trial_badge")}
              </div>
              <h2 className="text-3xl font-extrabold text-foreground">
                {t("landing.pricing_title")}
              </h2>
              <p className="mt-3 text-muted-foreground leading-relaxed">
                {t("landing.pricing_desc")}
              </p>

              <div className="mt-6 flex items-baseline gap-1">
                <span className="text-5xl font-extrabold text-foreground">€35</span>
                <div className="ml-1">
                  <span className="text-lg text-muted-foreground">{t("landing.pricing_month")}</span>
                  <p className="text-xs text-muted-foreground">{t("landing.pricing_tax")}</p>
                </div>
              </div>

              <Button size="lg" className="mt-8 h-12 px-8 text-base shadow-glow" asChild>
                <Link to="/signup">
                  {t("landing.pricing_cta")}
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
            </div>

            {/* Right: feature list */}
            <div className="flex-1">
              <p className="text-sm font-semibold text-foreground mb-4">{t("landing.pricing_includes")}</p>
              <ul className="grid gap-3 sm:grid-cols-2 md:grid-cols-1">
                {includedFeatures.map((feature, i) => (
                  <li key={i} className="flex items-center gap-2.5 text-sm text-foreground">
                    <div className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-accent/10">
                      <Check className="h-3 w-3 text-accent" />
                    </div>
                    {feature}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default PricingSection;
